import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getDealers, getInstallations } from 'src/bff';
import { isNull, useGetKvps, useIsComerciales } from 'src/feature-app';
import { arrayToObject, logMessage, sanitizeKvps } from 'src/helpers';
import { DealersData, Installation } from 'src/types';

export function useGetDealers(kvps?: string) {
	const dispatch = useDispatch();
	const [dealerName, setDealerName] = useState(null);
	const [selectedDealer, setSelectedDealer] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [dealers, setDealers] = useState<DealersData[]>(null);
	const { kvps: selectedKvps, isMyCurrentDealer } = useGetKvps(kvps);

	const getConcessions = async (dealers: DealersData[], dealer?: DealersData) => {
		if (dealer) {
			const concessionsFromDealer = dealers.filter((deal: DealersData) => {
				return deal.dmsInfo?.dealerId?.codConcesion == dealer.dmsInfo?.dealerId?.codConcesion;
			}) as DealersData[];
			logMessage(concessionsFromDealer, 'CONCESSIONS FROM DEALERS');

			if (concessionsFromDealer.length > 0) {
				dispatch({ type: 'SET_MY_CURRENT_DEALER', payload: dealer });
				return concessionsFromDealer;
			}
		} else {
			const isComerciales = useIsComerciales();
			const installationsResponse = await getInstallations(isComerciales ? 'LCV_DDB_FRM_LEM_REC_LEM' : 'VW_DDB_FRM_LEM_REC_LEM', 'VOLKSWAGEN_SF_LEAD_INSTALLATION_VN_VO_SERVICE');
			if (installationsResponse) {
				// Convertimos en un array manejable
				const installations = installationsResponse.map((instal) => arrayToObject(instal.attributes)) as Installation[];
				const installation = installations.find((instal: Installation) => {
					return sanitizeKvps(instal.KVPSCode__c) == sanitizeKvps(selectedKvps);
				}) as Installation;
				logMessage(installation, 'INSTALLATION');

				if (installation) {
					const concessionsFromInstallation = dealers.filter((deal: DealersData) => {
						return deal.dmsInfo?.dealerId?.codConcesion == installation?.VGED_ConcessionDealerCode__c;
					}) as DealersData[];
					logMessage(concessionsFromInstallation, 'CONCESSIONS FROM INSTALLATIONS');

					if (concessionsFromInstallation.length > 0) {
						dispatch({ type: 'SET_MY_CURRENT_INSTALLATION', payload: installation });
						return concessionsFromInstallation;
					}
				} else {
					console.warn('Installation not found');
				}
			} else {
				console.warn('Empty installations');
			}
		}
		return [];
	};

	const requestDealers = async () => {
		let dealers = await getDealers();

		if (!Array.isArray(dealers)) {
			setLoading(false);
			setError(true);
			console.error('Empty dealers');
			return;
		}

		dispatch({ type: 'DEALERS_OR_INSTALLATIONS_MODE', payload: 'dealers' });

		if (selectedKvps) {
			// Buscamos dealer por kvps. O bien buscamos por codInstalacion siempre que no sea isMyCurrentDealer.
			const dealer = dealers.find((dealer) => dealer.kvps == selectedKvps || (dealer.codInstalacion == selectedKvps && !isMyCurrentDealer));
			logMessage(dealer, 'DEALER');

			// Si venimos del service MyDealer.currentDealer, guardamos la lista de concesiones como dealers.
			if (isMyCurrentDealer) {
				// Agrupamos por concession. Si dealer es null, buscamos en getInstallations()
				const concessions = await getConcessions(dealers, dealer);

				if (concessions.length > 0) {
					dealers = concessions;
				}
			}

			if (dealer) {
				setDealerName(dealer.name);
				setSelectedDealer(dealer);

				if (!isMyCurrentDealer) {
					dispatch({ type: 'SET_SELECTED_DEALER', payload: dealer });
				}
				//dispatch({ type: 'UPDATE_SUGGESTION', payload: dealer });
			} else {
				setDealerName(null);
				setSelectedDealer(null);
				console.warn('Dealer not found');
			}
		}

		const points = dealers
			.map((dealer: DealersData) => {
				if (isNull(dealer.markerInfo.svcLocation)) return {};
				const { longitude, latitude } = dealer.markerInfo.svcLocation;
				return {
					type: 'Feature',
					properties: { cluster: false, concesionId: dealer.id, dealer: dealer },
					geometry: {
						type: 'Point',
						coordinates: [longitude, latitude],
					},
				};
			})
			.filter((point) => Object.keys(point).length > 0);

		setDealers(dealers);
		dispatch({ type: 'SET_DEALERS', payload: dealers });
		dispatch({ type: 'SET_POINTS', payload: points });
		setLoading(false);
	};

	useEffect(() => {
		requestDealers();
	}, []);

	return { dealers, dealerName, selectedDealer, loading, error };
}
